import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityMessageType,
    ProductPriceDto,
    WishListLineModel,
} from "@insite/client-framework/Types/ApiModels";
import {
    MGBW_CONFIGURATION_ITEM_NUMBER,
    MGBW_IS_CONFIGURABLE,
    MGBW_SHIPPING_AVAILABILITY,
} from "@tSurya/Common/MgbwPropertyKeys";
import { getVisibleMgbwSelections } from "@tSurya/Common/MgbwConfiguratorHelpers";
import parseCurrencyDisplay from "@tSurya/Common/Utilities/parseCurrencyDisplay";
import PrintBrandBadge from "@tSurya/Components/PrintBrandBadge";
import { getActiveThemeId, THEME_IDS, ThemeId } from "@tSurya/theme/themeHelper";
import { getIsUkWebsite, normalizeSourceBrand } from "@tSurya/Common/Utilities/websiteUtils";
import React from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import styled from "styled-components";

interface OwnProps {
    includePrices: boolean;
}

interface StateProps {
    wishList: ReturnType<typeof getWishListState>["value"];
    wishListLinesDataView: ReturnType<typeof getWishListLinesDataView>;
    productInfosByWishListLineId: SafeDictionary<ProductInfo>;
    billTo: ReturnType<typeof getCurrentBillToState>["value"];
    isUkWebsite: boolean;
    sessionCurrencySymbol: string;
}

const mapStateToProps = (state: ApplicationState): StateProps => {
    const wishListId = state.pages.myListDetails.wishListId;
    return {
        wishList: getWishListState(state, wishListId).value,
        wishListLinesDataView: getWishListLinesDataView(
            state,
            state.pages.myListDetails.loadWishListLinesParameter,
        ),
        productInfosByWishListLineId: state.pages.myListDetails.productInfosByWishListLineId,
        billTo: getCurrentBillToState(state).value,
        isUkWebsite: getIsUkWebsite(state),
        sessionCurrencySymbol: state.context.session.currency?.currencySymbol || "",
    };
};

type Props = OwnProps & StateProps;

const getLinePricing = (
    wishListLine: WishListLineModel,
    productInfo?: ProductInfo,
): ProductPriceDto | null => productInfo?.pricing ?? wishListLine.pricing ?? null;

const getInventoryAvailability = (productInfo?: ProductInfo) => {
    const unitOfMeasure = productInfo?.unitOfMeasure?.toLowerCase();
    const availability = productInfo?.inventory?.inventoryAvailabilityDtos?.find(
        entry => entry.unitOfMeasure.toLowerCase() === unitOfMeasure,
    )?.availability;

    return availability?.messageType !== AvailabilityMessageType.NoMessage ? availability?.message || "" : "";
};

const getLineProductName = (wishListLine: WishListLineModel) => {
    if (wishListLine.properties?.[MGBW_IS_CONFIGURABLE] === "true") {
        return wishListLine.shortDescription || wishListLine.productName;
    }

    const normalizedErpNumber = wishListLine.erpNumber.trim().toLowerCase();
    const parentProductName = wishListLine.properties?.parentProductErpNumber || "";
    const normalizedParentProductName = parentProductName.endsWith("_P")
        ? parentProductName.slice(0, -2)
        : parentProductName;
    const descriptiveName = [wishListLine.productName, normalizedParentProductName, wishListLine.shortDescription].find(
        value => value && value.trim().toLowerCase() !== normalizedErpNumber,
    );

    return descriptiveName || wishListLine.productName || wishListLine.shortDescription || wishListLine.erpNumber;
};

const formatCurrency = (value: number, symbol: string) =>
    `${symbol}${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Brand identity for the print header, selected from the active site/theme so the document
// shows the correct active brand in the nav and the matching centered wordmark.
interface BrandHeaderConfig {
    navLabel: string;
    wordmark: React.ReactNode;
}

const BRAND_HEADER_CONFIG: Record<ThemeId, BrandHeaderConfig> = {
    [THEME_IDS.SURYA]: {
        navLabel: "Surya",
        wordmark: "SURYA",
    },
    [THEME_IDS.GLOBAL_VIEWS]: {
        navLabel: "Global Views",
        wordmark: "GLOBAL VIEWS",
    },
    [THEME_IDS.MGBW]: {
        navLabel: "Mitchell Gold + Bob Williams",
        wordmark: (
            <>
                Mitchell Gold
                <br />
                +Bob Williams
            </>
        ),
    },
};

const BRAND_NAV_ORDER: ThemeId[] = [THEME_IDS.SURYA, THEME_IDS.GLOBAL_VIEWS, THEME_IDS.MGBW];

// Resolves the displayed line total, falling back to unit price * quantity when the
// extended total display is unavailable.
const getLineTotal = (
    pricing: ProductPriceDto | null,
    quantity: number,
): number => {
    if (!pricing) {
        return 0;
    }
    const extended = parseCurrencyDisplay(pricing.extendedUnitNetPriceDisplay);
    if (extended > 0) {
        return extended;
    }
    return parseCurrencyDisplay(pricing.unitNetPriceDisplay) * (quantity || 0);
};

// Print-only layout. Rendered through a portal as a direct child of <body> and hidden on
// screen. In print, every other direct child of <body> is removed from layout via display:none
// so only this clean document is rendered (display:none avoids the blank trailing pages that
// the visibility:hidden trick produces because hidden elements still occupy layout space).
const PrintRoot = styled.div`
    display: none;

    @media print {
        display: block;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        color: #000;
        font-family: "Times New Roman", Georgia, serif;
        padding: 0 24px;
        box-sizing: border-box;
    }
`;

const GlobalPrintHide = styled.div`
    display: none;
`;

const BrandNav = styled.div`
    display: flex;
    justify-content: center;
    gap: 28px;
    font-size: 12px;
    letter-spacing: 1px;
    color: #9b9b9b;
    text-transform: uppercase;
    margin-bottom: 18px;
`;

const ActiveBrand = styled.span`
    color: #333;
    font-weight: 600;
`;

const Wordmark = styled.div`
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.1;
    color: #1a1a1a;
    margin-bottom: 24px;
`;

const ProjectTitle = styled.div`
    text-align: center;
    font-size: 30px;
    margin: 8px 0 18px;
    color: #222;
`;

const MetaRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    font-size: 12px;
    border-bottom: 1px solid #cfcfcf;
    padding-bottom: 8px;
    margin-bottom: 4px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
`;

const Th = styled.th<{ align?: string }>`
    text-align: ${({ align }) => align || "left"};
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
    color: #444;
    border-bottom: 1px solid #cfcfcf;
    padding: 8px 6px;
    font-weight: 600;
`;

const Td = styled.td<{ align?: string }>`
    text-align: ${({ align }) => align || "left"};
    vertical-align: top;
    padding: 14px 6px;
    border-bottom: 1px solid #e6e6e6;
`;

const ItemCell = styled.div`
    display: flex;
    gap: 18px;
`;

const ProductMedia = styled.div`
    width: 96px;
    flex: 0 0 96px;

    > div {
        margin-top: 6px;
    }
`;

const ProductImg = styled.img`
    width: 100%;
    height: auto;
    object-fit: contain;
    display: block;
`;

const ItemName = styled.div`
    font-weight: 700;
    margin-bottom: 8px;
`;

const SpecRow = styled.div`
    display: flex;
    gap: 8px;
    font-size: 11px;
    line-height: 1.5;
`;

const SpecLabel = styled.span`
    color: #333;
    min-width: 72px;
`;

const SpecValue = styled.span`
    color: #555;
`;

const SubtotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 13px;
    padding: 16px 6px;
    border-top: 2px solid #333;
    margin-top: 4px;
`;

const PrintProjectView: React.FC<Props> = ({
    includePrices,
    wishList,
    wishListLinesDataView,
    productInfosByWishListLineId,
    billTo,
    isUkWebsite,
    sessionCurrencySymbol,
}) => {
    if (!wishList || !wishListLinesDataView.value) {
        return null;
    }

    const lines = wishListLinesDataView.value;
    const activeThemeId = getActiveThemeId();
    const currencySymbol = isUkWebsite
        ? sessionCurrencySymbol || "£"
        : billTo?.customerCurrencySymbol || sessionCurrencySymbol || "$";
    const accountNumber = billTo?.customerNumber || "";
    const accountName = billTo?.customerName || wishList.name;
    const itemCount = wishList.wishListLinesCount || lines.length;

    const subtotal = lines.reduce((sum, line) => {
        const pricing = getLinePricing(line, productInfosByWishListLineId[line.id]);
        return sum + getLineTotal(pricing, line.qtyOrdered);
    }, 0);

    if (typeof document === "undefined") {
        return null;
    }

    const content = (
        <>
            <GlobalPrintHide
                dangerouslySetInnerHTML={{
                    __html: `<style>
                        @media print {
                            body > *:not(#sur-print-project-root) { display: none !important; }
                        }
                    </style>`,
                }}
            />
            <PrintRoot id="sur-print-project-root">
                {isUkWebsite ? (
                    <Wordmark>{translate("Surya UK")}</Wordmark>
                ) : (
                    <>
                        <BrandNav>
                            {BRAND_NAV_ORDER.map(themeId => {
                                const config = BRAND_HEADER_CONFIG[themeId];
                                return themeId === activeThemeId ? (
                                    <ActiveBrand key={themeId}>{translate(config.navLabel)}</ActiveBrand>
                                ) : (
                                    <span key={themeId}>{translate(config.navLabel)}</span>
                                );
                            })}
                        </BrandNav>
                        <Wordmark>{BRAND_HEADER_CONFIG[activeThemeId].wordmark}</Wordmark>
                    </>
                )}
                <ProjectTitle>{wishList.name}</ProjectTitle>
                <MetaRow>
                    <div>
                        {accountNumber && (
                            <div>
                                {translate("Account Number")} : {accountNumber}
                            </div>
                        )}
                        {accountName && (
                            <div>
                                {translate("Name")} : {accountName}
                            </div>
                        )}
                    </div>
                    <div>
                        {itemCount} {itemCount === 1 ? translate("item") : translate("items")}
                    </div>
                </MetaRow>

                <Table>
                    <thead>
                        <tr>
                            <Th>{translate("Item")}</Th>
                            {includePrices && <Th align="right">{translate("Unit Price")}</Th>}
                            <Th align="right">{translate("Qty")}</Th>
                            {includePrices && <Th align="right">{translate("Total")}</Th>}
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map(line => {
                            const productInfo = productInfosByWishListLineId[line.id];
                            const pricing = getLinePricing(line, productInfo);
                            const lineTotal = getLineTotal(pricing, line.qtyOrdered);
                            const itemNumber =
                                line.properties?.[MGBW_CONFIGURATION_ITEM_NUMBER] || line.erpNumber || "";
                            const productName = getLineProductName(line);
                            const isConfigurable = line.properties?.[MGBW_IS_CONFIGURABLE] === "true";
                            const showItemNumber =
                                !!itemNumber && itemNumber.trim().toLowerCase() !== productName.trim().toLowerCase();
                            const variantSize = line.properties?.variantSize || "";
                            const isSuryaProduct = (normalizeSourceBrand(line.properties?.sourceBrand) || "surya") === "surya";
                            const selections = getVisibleMgbwSelections(line.properties);
                            const shippingAvailability = line.properties?.[MGBW_SHIPPING_AVAILABILITY] || "";
                            const inventoryAvailability = shippingAvailability
                                ? ""
                                                                : line.properties?.mgbw_isSwatch === "true"
                                                                    ? translate("In Stock")
                                                                    : getInventoryAvailability(productInfo);
                            return (
                                <tr key={line.id}>
                                    <Td>
                                        <ItemCell>
                                            <ProductMedia>
                                                {line.smallImagePath && (
                                                    <ProductImg src={line.smallImagePath} alt={line.shortDescription} />
                                                )}
                                                <PrintBrandBadge brandName={line.properties?.sourceBrand} />
                                            </ProductMedia>
                                            <div>
                                                <ItemName>{productName}</ItemName>
                                                {showItemNumber && (
                                                    <SpecRow>
                                                        {isConfigurable && <SpecLabel>{translate("Item #")}</SpecLabel>}
                                                        <SpecValue>{itemNumber}</SpecValue>
                                                    </SpecRow>
                                                )}
                                                {!isConfigurable && !isSuryaProduct && variantSize && (
                                                    <SpecRow>
                                                        <SpecValue>{variantSize}</SpecValue>
                                                    </SpecRow>
                                                )}
                                                {selections.map(entry => (
                                                    <SpecRow key={entry.OptionId}>
                                                        <SpecLabel>{entry.Name}</SpecLabel>
                                                        <SpecValue>{entry.Value}</SpecValue>
                                                    </SpecRow>
                                                ))}
                                                {shippingAvailability && (
                                                    <SpecRow>
                                                        <SpecValue>{shippingAvailability}</SpecValue>
                                                    </SpecRow>
                                                )}
                                            </div>
                                        </ItemCell>
                                    </Td>
                                    {includePrices && (
                                        <Td align="right">
                                            <div>{pricing?.unitNetPriceDisplay ?? ""}</div>
                                            {inventoryAvailability && <div>{inventoryAvailability}</div>}
                                        </Td>
                                    )}
                                    <Td align="right">{line.qtyOrdered}</Td>
                                    {includePrices && (
                                        <Td align="right">{formatCurrency(lineTotal, currencySymbol)}</Td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>

                {includePrices && (
                    <SubtotalRow>
                        <span>{translate("Subtotal")}</span>
                        <span>{formatCurrency(subtotal, currencySymbol)}</span>
                    </SubtotalRow>
                )}
            </PrintRoot>
        </>
    );

    return ReactDOM.createPortal(content, document.body);
};

export default connect(mapStateToProps)(PrintProjectView);
